import React, { useCallback, useEffect } from 'react';
import {
  Button,
  HeadingText,
  Image,
  Modal,
  ParagraphText,
  queryHelpers,
  StackLayout,
  useMediaQuery,
  VisuallyHidden,
} from '@leagueplatform/genesis-core';
import { useIntl } from '@leagueplatform/locales';
import { EVENT_NAME, trackAnalyticsEvent } from '@leagueplatform/analytics';
import { useNavigate } from '@leagueplatform/routing';
import { APP_MANAGED_ROUTES } from 'common/app-managed-routes';
import { PRODUCT_AREAS, SUB_PRODUCT_AREAS } from 'common/constants';
import doctorIcon from 'assets/doctor.svg';

type CareManagementCohortModalProps = {
  body?: string;
  onClose: () => void;
};

export const CASE_MANAGEMENT_MODAL_SCREEN_NAME =
  'case management outreach modal';

export const CareManagementCohortModal = ({
  body,
  onClose,
}: CareManagementCohortModalProps) => {
  const { formatMessage } = useIntl();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(queryHelpers.down('mobileLandscape'));

  useEffect(() => {
    trackAnalyticsEvent(EVENT_NAME.SCREEN_LOADED, {
      product_area: PRODUCT_AREAS.BENEFITS,
      sub_product_area: SUB_PRODUCT_AREAS.ADDITIONAL_BENEFITS,
      screen_name: CASE_MANAGEMENT_MODAL_SCREEN_NAME,
    });
  }, []);

  const handleClose = useCallback(() => {
    trackAnalyticsEvent(EVENT_NAME.BUTTON_CLICKED, {
      product_area: PRODUCT_AREAS.BENEFITS,
      sub_product_area: SUB_PRODUCT_AREAS.ADDITIONAL_BENEFITS,
      screen_name: CASE_MANAGEMENT_MODAL_SCREEN_NAME,
      detail: 'dismiss',
    });
    onClose();
  }, [onClose]);

  const handleClick = useCallback(
    (event: React.MouseEvent) => {
      event.preventDefault();
      trackAnalyticsEvent(EVENT_NAME.BUTTON_CLICKED, {
        product_area: PRODUCT_AREAS.BENEFITS,
        sub_product_area: SUB_PRODUCT_AREAS.ADDITIONAL_BENEFITS,
        screen_name: CASE_MANAGEMENT_MODAL_SCREEN_NAME,
        detail: 'learn more',
      });
      window.history.replaceState(null, '', window.location.pathname);
      navigate(APP_MANAGED_ROUTES.CASE_MANAGEMENT_PROGRAM);
    },
    [navigate],
  );

  return (
    <Modal.Root
      defaultOpen
      onOpenChange={(open) => {
        if (!open) {
          handleClose();
        }
      }}
    >
      <Modal.Content
        showCloseButton
        closeOnOverlay
        layout={isMobile ? 'fullscreen' : 'standard'}
        width={isMobile ? '100%' : '375px'}
        css={{
          '.GDS-modal': {
            height: 'auto',
            zIndex: 7000,
          },
          '.GDS-modal-overlay': {
            zIndex: 7000,
          },
        }}
        padding="$three $oneAndHalf $five"
      >
        <Modal.Title>
          <VisuallyHidden>
            {formatMessage({ id: 'CARE_MANAGEMENT_PROGRAM' })}
          </VisuallyHidden>
        </Modal.Title>

        <StackLayout horizontalAlignment="center">
          <Image
            css={{
              width: 80,
              height: 80,
              marginBottom: '$oneAndHalf',
            }}
            role="presentation"
            src={doctorIcon}
          />

          <HeadingText
            size="lg"
            level="2"
            css={{
              lineHeight: '150%',
              marginBottom: '$one',
              fontWeight: 'semibold',
            }}
          >
            {formatMessage({ id: 'HERE_TO_HELP' })}
          </HeadingText>

          <ParagraphText
            size="sm"
            css={{
              lineHeight: '160%',
              textAlign: 'center',
              marginBottom: '$oneAndHalf !important',
            }}
          >
            {formatMessage({ id: body })}
          </ParagraphText>

          <Modal.Close asChild>
            <Button
              as="a"
              size="large"
              priority="primary"
              width="fillContainer"
              href={APP_MANAGED_ROUTES.CASE_MANAGEMENT_PROGRAM}
              onClick={handleClick}
            >
              {formatMessage({ id: 'LEARN_MORE' })}
            </Button>
          </Modal.Close>
        </StackLayout>
      </Modal.Content>
    </Modal.Root>
  );
};
